import React, { useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import { formatFigure } from 'Helpers/formatService';

import Tags from 'CommonComponents/Tags';
import Tooltip from 'CommonComponents/Tooltip';

import CircleCheckIcon from 'Assets/icons/circle-check.svg';
import EditIcon from 'Assets/icons/edit.svg';
import StarFilledIcon from 'Assets/icons/star-filled.svg';
import StarIcon from 'Assets/icons/star.svg';
import TrashIcon from 'Assets/icons/trash.svg';

export default function GeneralDecisionsTable(props) {
    const [decisions, setDecisions] = useState([]);
    const decisionsListRef = useRef();

    const columnsCount = useMemo(() => {
        const count = 11;
        if (props.showSourceColumn) {
            return count + 1;
        }
        if (props.impactCategories) {
            return count + props.impactCategories.length;
        }
        return count;
    }, [props.showSourceColumn, props.impactCategories]);

    useEffect(() => {
        setDecisions(props.decisions);
    }, [props.decisions]);

    useEffect(() => {
        if (decisionsListRef.current) {
            decisionsListRef.current.scrollTop = decisionsListRef.current.scrollHeight;
        }
    }, [props.decisions.length]);

    function handleOnDragEnd(result) {
        if (!result.destination) {
            return;
        }

        const newDecisions = [...decisions];
        const [reorderedDecisions] = newDecisions.splice(result.source.index, 1);
        newDecisions.splice(result.destination.index, 0, reorderedDecisions);
        setDecisions(newDecisions);

        axios.post(props.reorderUrl, {
            decisions: newDecisions.map((decision) => decision.id),
        });
    }

    return (
        <div className="inbud-table_wrapper">
            <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="decisionsList">
                    {(provided) => (
                        <div
                            className="tw-overflow-y-auto tw-max-h-[60vh]"
                            ref={(el) => {
                                provided.innerRef(el);
                                decisionsListRef.current = el;
                            }}
                            {...provided.droppableProps}
                        >
                            <table className="inbud-table tw-table-fixed">
                                <thead>
                                    <tr>
                                        <th className="tw-sticky tw-top-0 tw-left-0 tw-bg-gray-100 tw-w-32 tw-z-10">{$t('Reference')}</th>
                                        {props.showSourceColumn && (
                                            <th className="tw-sticky tw-top-0 tw-left-32 tw-bg-gray-100 tw-w-64 tw-z-10">{$t('Source')}</th>
                                        )}
                                        <th
                                            className={classNames('tw-sticky tw-top-0 tw-bg-gray-100 tw-w-96 tw-z-10', {
                                                'tw-left-96': props.showSourceColumn,
                                                'tw-left-32': !props.showSourceColumn,
                                            })}
                                        >
                                            {$t('Description')}
                                        </th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Owner')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Status')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Priority')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('No. of Actions')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Actions - Completed')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Actions - Outstanding')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Target Decision Date')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Date Decision Made')}</th>
                                        {props.impactCategories?.map((impactCategory) => (
                                            <th className="tw-sticky tw-top-0 tw-w-32" key={impactCategory?.id}>
                                                {impactCategory.title}
                                            </th>
                                        ))}
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Actions')}</th>
                                    </tr>
                                </thead>

                                {!props.isLoading && !!decisions?.length && (
                                    <tbody>
                                        {decisions.map((decision, index) => (
                                            <Draggable key={decision.id} draggableId={decision.id.toString()} index={index}>
                                                {(provided) => (
                                                    <GeneralDecisionRow
                                                        key={decision.id}
                                                        showSourceColumn={props.showSourceColumn}
                                                        decision={decision}
                                                        impactCategories={props.impactCategories}
                                                        showUrl={props.showUrl}
                                                        onStar={props.onStar}
                                                        onEdit={props.onEdit}
                                                        onDelete={props.onDelete}
                                                        canManage={props.canManage}
                                                        provided={provided}
                                                    />
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </tbody>
                                )}
                                {props.isLoading && (
                                    <tbody>
                                        <tr>
                                            <td colSpan={columnsCount}>{$t('Loading')}...</td>
                                        </tr>
                                    </tbody>
                                )}
                                {!props.isLoading && !props.decisions?.length && (
                                    <tbody>
                                        <tr>
                                            <td colSpan={columnsCount}>
                                                <span className="mr-3 font-italic">{$t('No Decisions found')}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                )}
                            </table>
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    );
}

function GeneralDecisionRow({ showSourceColumn, decision, impactCategories, showUrl, onStar, onEdit, onDelete, provided, ...props }) {
    const sourceNames = useMemo(() => {
        if (!decision.source_names) {
            return [$t('Central')];
        }
        try {
            return JSON.parse(decision.source_names);
        } catch (e) {
            console.error('Error parsing source names for decision ID ' + decision.id, e);
            return null;
        }
    }, [decision.source_names]);

    return (
        <tr ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
            <td className={'tw-sticky tw-left-0 tw-bg-gray-100 tw-z-5'}>{decision.id}</td>
            {showSourceColumn && <td className="tw-sticky tw-left-32 tw-bg-gray-100 tw-z-5">{<Tags tags={sourceNames} />}</td>}
            <td className={classNames('tw-sticky tw-bg-gray-100 tw-z-5', { 'tw-left-96': showSourceColumn, 'tw-left-32': !showSourceColumn })}>
                <Tooltip placement="auto" tooltipContent={decision.detailed_description ? decision.detailed_description : '-'} tooltipType="text">
                    <div className="tw-flex tw-space-x-1 tw-w-fit">
                        <div className="tw-text-blue">{decision.description}</div>
                    </div>
                </Tooltip>
            </td>
            <td>
                {decision.owner?.first_name} {decision.owner?.last_name}
            </td>
            <td>{decision.status?.title}</td>
            <td className="tw-capitalize">{decision.priority}</td>
            <td className="inbud-numeric-cell">{formatFigure(decision.actions_count?.total, String)}</td>
            <td className="inbud-numeric-cell">{formatFigure(decision.actions_count?.completed, String)}</td>
            <td className="inbud-numeric-cell">{formatFigure(decision.actions_count?.outstanding, String)}</td>
            <td className="inbud-date-cell">{decision.target_date}</td>
            <td className="inbud-date-cell">{decision.date}</td>
            {impactCategories?.map((impactCategory) => (
                <td key={impactCategory?.id}>
                    <div className="tw-flex tw-items-center tw-justify-center">
                        {decision.impact_category_ids?.includes(impactCategory?.id) && <CircleCheckIcon className="tw-w-5 tw-h-5 tw-text-green" />}
                    </div>
                </td>
            ))}
            <td>
                <div className="tw-flex tw-items-center tw-gap-x-3">
                    <button type="button" onClick={() => onStar(decision)}>
                        {decision.is_hot ? <StarFilledIcon className="tw-w-3 tw-h-3 tw-text-blue" /> : <StarIcon className="tw-w-3 tw-h-3" />}
                    </button>
                    {!!props.canManage && (
                        <>
                            <button type="button" onClick={() => onEdit(decision)}>
                                <EditIcon className="tw-w-3 tw-h-3" />
                            </button>
                            <button type="button" className="tw-text-red" onClick={() => onDelete(decision)}>
                                <TrashIcon className="tw-w-3 tw-h-3" />
                            </button>
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
}
